import { useQueries, useQuery } from "@tanstack/react-query";

import { getJson } from "../../lib/http";
import { qk } from "../../lib/queryKeys";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useCompanyDemoFlag } from "../companies/demoFlags";
import type {
  CentinelaBitacoraResponse,
  CentinelaConduccionResponse,
  CentinelaParteResponse,
  CentinelaUnitConfig,
  CentinelaCallsResponse,
  CentinelaCase,
  CentinelaCasesQuery,
  CentinelaCasesResponse,
  CentinelaCatalogs,
  CentinelaCallRecordingResponse,
  CentinelaCaseMediaResponse,
  CentinelaEventsResponse,
  CentinelaGeofenceSuggestionsJob,
  CentinelaNumerosResponse,
  CentinelaSummary,
  CentinelaSupervisoresResponse,
  CentinelaUnitMediaResponse,
  CentinelaUnitResponse,
  CentinelaUnitsResponse,
  CentinelaUsersResponse,
  CentinelaVocesResponse,
} from "./types";

const CATALOGS_STALE_MS = 60 * 60 * 1000;
const SCREEN_GC_MS = 30 * 60 * 1000;
const SUGGESTION_JOB_POLL_MS = 8000;
const SUGGESTION_JOB_MAX_POLLS = 45;

const RECORDING_STALE_MS = 10 * 60 * 1000;
const CASE_MEDIA_STALE_MS = 5 * 60 * 1000;

/*
  Cada cuánto se vuelve a pedir el parte de conducción mientras la pantalla
  está abierta.

  No es por frescura del dato: es por las URLs de la evidencia. Centinela las
  firma con `getSignedUrl` v4 y una vigencia de **900 segundos** (ver
  `cases/media.js`, `ttlSec = 900`). Pasado ese cuarto de hora cada miniatura
  responde 403 y la columna de capturas se llena de imágenes rotas sin que nada
  falle: la respuesta sigue en la caché, lo que caducó son los enlaces.

  `staleTime: 0` no alcanzaba porque sólo refresca al montar y al volver a la
  pestaña; una pantalla que se queda abierta —que es justo lo que se hace con un
  expediente— nunca dispara ninguno de los dos. Diez minutos deja margen de
  sobra por debajo de los quince.
*/
const CONDUCCION_REFRESCO_MS = 10 * 60 * 1000;

const SCREEN_QUERY = {
  staleTime: 0,
  gcTime: SCREEN_GC_MS,
  retry: 0,
} as const;

export const CENTINELA_JOB_READY = "listo";

export const isCentinelaJobSettled = (
  job: CentinelaGeofenceSuggestionsJob | undefined,
): boolean => Boolean(job && (job.estado === CENTINELA_JOB_READY || job.error));

interface Envelope<T> {
  data: T;
}

interface CentinelaCasesEnvelope {
  cases?: CentinelaCase[];
  casos?: CentinelaCase[];
  total?: number;
}

export async function fetchCentinela<T>(
  url: string,
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const body = await getJson<Envelope<T>>(url, params, { signal });
  return body.data;
}

function useDemoParams() {
  const isDemo = useCompanyDemoFlag("centinela_demo");
  return isDemo ? { demo: true } : undefined;
}

export function useCentinelaCatalogs() {
  return useQuery({
    queryKey: qk.centinela.catalogs(),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaCatalogs>("/centinela/catalogs", undefined, signal),
    staleTime: CATALOGS_STALE_MS,
  });
}

export function useCentinelaUnits() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.units(companyId, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaUnitsResponse>("/centinela/units", demo, signal),
    enabled: Boolean(companyId),
    ...SCREEN_QUERY,
  });
}

/**
 * La evidencia real de un caso: las fotos y clips que subió la cámara.
 *
 * Las URL vienen firmadas por Centinela y caducan, así que la consulta se
 * vuelve a pedir cuando el caso se reabre en pantalla en vez de reusar una
 * respuesta vieja: una URL vencida se ve como una imagen rota, y eso se lee
 * como «la cámara falló».
 */
export function useCentinelaCaseMedia(caseId: string | null) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.caseMedia(companyId, caseId, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaCaseMediaResponse>(
        `/centinela/cases/${caseId}/media`,
        demo,
        signal,
      ),
    enabled: Boolean(companyId && caseId),
    staleTime: CASE_MEDIA_STALE_MS,
    gcTime: SCREEN_GC_MS,
    retry: 0,
  });
}

export function useCentinelaUnit(deviceId: string | null) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.unit(companyId, deviceId, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaUnitResponse>(
        `/centinela/units/${deviceId}`,
        demo,
        signal,
      ),
    enabled: Boolean(deviceId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaSummary() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.summary(companyId, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaSummary>("/centinela/summary", demo, signal),
    enabled: Boolean(companyId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaUnitCalls(deviceId: string | null) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.unitCalls(companyId, deviceId, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaCallsResponse>(
        `/centinela/units/${deviceId}/calls`,
        demo,
        signal,
      ),
    enabled: Boolean(deviceId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaUnitEvents(deviceId: string | null) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.unitEvents(companyId, deviceId, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaEventsResponse>(
        `/centinela/units/${deviceId}/events`,
        demo,
        signal,
      ),
    enabled: Boolean(deviceId),
    ...SCREEN_QUERY,
  });
}

/**
 * Lo que grabó la cámara de la unidad, haya caso o no.
 *
 * Las URL vienen firmadas y caducan, así que se vuelve a pedir en vez de
 * reusar una respuesta vieja: una URL vencida se ve como una imagen rota.
 */
/**
 * Lo que grabó la cámara de una unidad.
 *
 * ── `fecha` gana sobre `horas` ──────────────────────────────────────────────
 * Con `horas` sola, la ventana se cuenta hacia atrás desde AHORA, y eso hacía
 * que cada día que pasaba se cayera un día de evidencia de la pantalla aunque
 * siguiera guardada: la cámara subía ciento sesenta archivos un jueves y el
 * viernes por la tarde la pestaña decía «no ha subido nada», porque todos
 * habían quedado fuera de las 24 h.
 *
 * Con `fecha` se pide un día concreto, que además es como está guardado —un
 * documento por unidad y por día— así que cuesta una lectura en vez de cuatro.
 */
export function useCentinelaUnitMedia(
  deviceId: string | null,
  { horas = 24, fecha = null }: { horas?: number; fecha?: string | null } = {},
) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  // Los dos parámetros entran en la llave: sin `fecha` ahí, cambiar de día
  // devolvería la respuesta cacheada del día anterior.
  const params = fecha ? { fecha } : { horas };

  return useQuery({
    queryKey: qk.centinela.unitMedia(companyId, deviceId, Boolean(demo), fecha ?? horas),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaUnitMediaResponse>(
        `/centinela/units/${deviceId}/media`,
        { ...demo, ...params },
        signal,
      ),
    enabled: Boolean(deviceId),
    staleTime: CASE_MEDIA_STALE_MS,
    gcTime: SCREEN_GC_MS,
    retry: 0,
  });
}

/**
 * La bitácora de una unidad: los hechos que no abrieron caso.
 *
 * Se pide por ventana de horas y no por día, porque quien mira la tarjeta de
 * una unidad quiere lo reciente, no la jornada completa.
 */
/**
 * El parte del día de una unidad: cómo se movió, qué le pasó y cómo se manejó.
 *
 * NO exige cámara. Una unidad con GPS a secas trae movimientos, incidencias y
 * llamadas; la de cámara además trae las conductas al volante. Es la misma
 * pantalla con más o menos detalle, no dos pantallas distintas — condicionarla
 * al equipo dejaría sin historial a la mayor parte de la flota.
 */
/**
 * El día de conducción de una unidad.
 *
 * Reemplaza a `useCentinelaUnitParte` en la tarjeta: es el mismo día pero con
 * los hechos ya ubicados y la medición de kilómetros y tiempos. El mismo hook
 * lo va a usar el mapa, pidiendo además el recorrido.
 *
 * NO exige cámara. Una unidad con GPS a secas trae kilómetros, tiempos,
 * movimientos e incidencias; la de cámara suma las conductas y la evidencia.
 */
/**
 * El dia de conduccion de una unidad, en dos sabores que NO se mezclan.
 *
 * `fecha` pide un dia natural, de medianoche a medianoche en la zona de la
 * empresa: es la pregunta de quien audita, y es la que usa la pestana
 * Conduccion con su selector de fecha.
 *
 * `horas` pide las ultimas N horas contadas desde ahora, y puede cruzar la
 * medianoche: es la pregunta de quien vigila, y es la que necesita el rastro
 * del mapa. Sin esto, a las 00:30 pedir «las ultimas tres horas» devolvia un
 * dia natural que empezaba a las 00:00 y el mapa aparecia vacio hacia atras.
 *
 * Mandar las dos es una contradiccion y la API contesta 400, asi que `horas`
 * solo viaja cuando no hay `fecha`.
 */
/**
 * Las opciones de la consulta del parte de conducción, sin el hook.
 *
 * Existen aparte porque la pantalla pide un DÍA con `useQuery` y un PERIODO con
 * `useQueries` —Centinela sólo sirve por día, así que un rango son N
 * peticiones— y las dos tienen que compartir llave de caché, parámetros y
 * refresco. Con el hook metido dentro no se podían reutilizar: `useQueries`
 * recibe opciones, no hooks.
 */
export function opcionesDeConduccion({
  companyId,
  deviceId,
  demo,
  fecha = "",
  horas = null,
  recorrido = false,
}: {
  companyId: string | null | undefined;
  deviceId: string | null;
  demo: ReturnType<typeof useDemoParams>;
  fecha?: string;
  horas?: number | null;
  recorrido?: boolean;
}) {
  const incluir = recorrido ? "recorrido" : "";
  const ventana = fecha ? null : horas;

  return {
    queryKey: qk.centinela.unitConduccion(
      companyId ?? null,
      deviceId,
      Boolean(demo),
      fecha,
      ventana,
      incluir,
    ),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchCentinela<CentinelaConduccionResponse>(
        `/centinela/units/${deviceId}/conduccion`,
        {
          ...demo,
          ...(fecha ? { fecha } : {}),
          ...(ventana ? { horas: String(ventana) } : {}),
          ...(incluir ? { incluir } : {}),
        },
        signal,
      ),
    enabled: Boolean(deviceId),
    ...SCREEN_QUERY,
    retry: 2,
    // Ver `CONDUCCION_REFRESCO_MS`: mantiene vivas las URLs firmadas de la
    // evidencia. En segundo plano no, que ahí nadie está mirando.
    refetchInterval: CONDUCCION_REFRESCO_MS,
    refetchIntervalInBackground: false,
  } as const;
}

export function useCentinelaUnitConduccion(
  deviceId: string | null,
  {
    fecha = "",
    horas = null,
    recorrido = false,
  }: { fecha?: string; horas?: number | null; recorrido?: boolean } = {},
) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery(opcionesDeConduccion({ companyId, deviceId, demo, fecha, horas, recorrido }));
}

/** Un parte por día: es lo único que Centinela sirve para un rango. */
export function useCentinelaConduccionPorDias(
  deviceId: string | null,
  dias: string[],
  { recorrido = false }: { recorrido?: boolean } = {},
) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQueries({
    queries: dias.map((fecha) =>
      opcionesDeConduccion({ companyId, deviceId, demo, fecha, recorrido }),
    ),
  });
}

export function useCentinelaUnitParte(
  deviceId: string | null,
  { fecha = "" }: { fecha?: string } = {},
) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.unitParte(companyId, deviceId, Boolean(demo), fecha),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaParteResponse>(
        `/centinela/units/${deviceId}/parte`,
        { ...demo, ...(fecha ? { fecha } : {}) },
        signal,
      ),
    enabled: Boolean(deviceId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaUnitBitacora(
  deviceId: string | null,
  { horas = 24, tipos = "" }: { horas?: number; tipos?: string } = {},
) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.unitBitacora(companyId, deviceId, Boolean(demo), horas, tipos),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaBitacoraResponse>(
        `/centinela/units/${deviceId}/bitacora`,
        { ...demo, horas, ...(tipos ? { tipos } : {}) },
        signal,
      ),
    enabled: Boolean(deviceId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaCases(query: CentinelaCasesQuery = {}) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  const params = Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== ""),
  );

  return useQuery({
    queryKey: qk.centinela.cases(companyId, params, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaCasesEnvelope>(
        "/centinela/cases",
        { ...params, ...demo },
        signal,
      ).then<CentinelaCasesResponse>((body) => ({
        cases: body?.cases ?? body?.casos ?? [],
        total: body?.total,
      })),
    enabled: Boolean(companyId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaConfig() {
  const companyId = useCompaniesStore((s) => s.companyId);

  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.config(companyId, Boolean(demo)),
    queryFn: ({ signal }) => fetchCentinela<unknown>("/centinela/config", demo, signal),
    enabled: Boolean(companyId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaUsage() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.usage(companyId, Boolean(demo)),
    queryFn: ({ signal }) => fetchCentinela<unknown>("/centinela/usage", demo, signal),
    enabled: Boolean(companyId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaSubscription() {
  const companyId = useCompaniesStore((s) => s.companyId);

  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.subscription(companyId, Boolean(demo)),
    queryFn: ({ signal }) => fetchCentinela<unknown>("/centinela/subscription", demo, signal),
    enabled: Boolean(companyId),
    staleTime: CATALOGS_STALE_MS,
  });
}

export function useCentinelaTripCustody(tripId: string | null) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.tripCustody(companyId, tripId),
    queryFn: ({ signal }) =>
      fetchCentinela<unknown>(`/centinela/trips/${tripId}/custody`, demo, signal),
    enabled: Boolean(tripId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaUnitCustody(deviceId: string | null) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.unitCustody(companyId, deviceId),
    queryFn: ({ signal }) =>
      fetchCentinela<unknown>(`/centinela/units/${deviceId}/custody`, demo, signal),
    enabled: Boolean(deviceId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaCallRecording(callId: string | null) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.callRecording(companyId, callId, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaCallRecordingResponse>(
        `/centinela/calls/${callId}/recording`,
        demo,
        signal,
      ),
    enabled: Boolean(callId),
    staleTime: RECORDING_STALE_MS,
    gcTime: RECORDING_STALE_MS,
    retry: 0,
  });
}

export function useCentinelaUnitConfig(deviceId: string | null) {
  const companyId = useCompaniesStore((s) => s.companyId);

  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.unitConfig(companyId, deviceId, Boolean(demo)),
    queryFn: ({ signal }) =>
      // Tipado en vez de `unknown`: quien lo consume necesita el teléfono, la
      // palabra clave y el perfil de custodia, y con `unknown` cada pantalla
      // acababa haciendo su propio cast.
      fetchCentinela<{ unit: CentinelaUnitConfig }>(
        `/centinela/config/units/${deviceId}`,
        demo,
        signal,
      ),
    enabled: Boolean(deviceId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaUnitConfigs(deviceIds: string[]) {
  const companyId = useCompaniesStore((s) => s.companyId);

  const demo = useDemoParams();

  return useQueries({
    queries: deviceIds.map((deviceId) => ({
      queryKey: qk.centinela.unitConfig(companyId, deviceId, Boolean(demo)),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        fetchCentinela<unknown>(`/centinela/config/units/${deviceId}`, demo, signal),
      enabled: Boolean(companyId),
      ...SCREEN_QUERY,
    })),
  });
}

export function useCentinelaGeofenceSuggestionJob(jobId: string | null) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.geofenceSuggestionJob(companyId, jobId, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaGeofenceSuggestionsJob>(
        `/centinela/geofence-suggestions/${jobId}`,
        demo,
        signal,
      ),
    enabled: Boolean(jobId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchInterval: (query) => {
      if (isCentinelaJobSettled(query.state.data)) return false;
      if (query.state.dataUpdateCount >= SUGGESTION_JOB_MAX_POLLS) return false;
      return SUGGESTION_JOB_POLL_MS;
    },
  });
}

export function useCentinelaNumeros() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.numeros(companyId, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaNumerosResponse>("/centinela/numeros", demo, signal),
    enabled: Boolean(companyId),
    staleTime: CATALOGS_STALE_MS,
  });
}

export function useCentinelaUsers() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.users(companyId, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaUsersResponse>("/centinela/users", demo, signal),
    enabled: Boolean(companyId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaSupervisores() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.supervisores(companyId, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaSupervisoresResponse>(
        "/centinela/supervisores",
        demo,
        signal,
      ),
    enabled: Boolean(companyId),
    ...SCREEN_QUERY,
  });
}

export function useCentinelaVoces() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useDemoParams();

  return useQuery({
    queryKey: qk.centinela.voces(companyId, Boolean(demo)),
    queryFn: ({ signal }) =>
      fetchCentinela<CentinelaVocesResponse>("/centinela/voces", demo, signal),
    enabled: Boolean(companyId),
    staleTime: CATALOGS_STALE_MS,
  });
}
